/**
 * Family Member Form Component
 * Feature: 004-restore-missing-features
 * Task: T016, T018
 *
 * Inline form for adding/editing family members
 * Used within PatientEditModal and PatientRegistrationModal
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Bell, BellOff } from 'lucide-react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PhoneInputWithDDI } from '@/features/shared/components/PhoneInputWithDDI'
import { extractLocalNumber } from '@/features/shared/utils/phoneUtils'
import { RELATIONSHIP_SELECT_OPTIONS } from '@/features/shared/constants/relationships'
import type { RelationshipType } from '@/features/shared/types/patient.types'
import {
  familyMemberFormSchema,
  type FamilyMemberFormData,
} from '../schemas/patient-form.schema'

// ============================================================================
// Types
// ============================================================================

interface FamilyMemberFormProps {
  initialData?: {
    name: string
    email?: string
    phone?: string
    country_code?: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications?: boolean
  }
  onSubmit: (data: {
    name: string
    email?: string
    phone?: string
    country_code: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications: boolean
  }) => void | Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
  isEditing?: boolean
  /** Number of existing primary contacts for this patient (used for max 2 validation) */
  existingPrimaryCount?: number
}

// ============================================================================
// Component
// ============================================================================

export function FamilyMemberForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isEditing = false,
  existingPrimaryCount = 0,
}: FamilyMemberFormProps) {
  // When editing, extract local number from whatsapp_number for display
  const getInitialPhone = () => {
    if (!initialData?.phone) return ''
    const countryCode = initialData.country_code ?? '55'
    return extractLocalNumber(initialData.phone, countryCode)
  }

  const form = useForm<FamilyMemberFormData>({
    resolver: zodResolver(familyMemberFormSchema),
    defaultValues: {
      name: initialData?.name ?? '',
      email: initialData?.email ?? '',
      phone: isEditing ? getInitialPhone() : (initialData?.phone ?? ''),
      country_code: initialData?.country_code ?? '55',
      relationship: initialData?.relationship ?? '',
      is_primary_contact: initialData?.is_primary_contact ?? false,
      accepts_notifications: initialData?.accepts_notifications ?? false,
    },
  })

  const watchPhone = form.watch('phone')
  const hasPhone = !!watchPhone && watchPhone.trim().length > 0

  const handleSubmit = async (data: FamilyMemberFormData) => {
    // Validate max 2 primary contacts
    if (data.is_primary_contact && !isEditing && existingPrimaryCount >= 2) {
      form.setError('is_primary_contact', {
        message: 'Maximo de 2 contatos principais por paciente',
      })
      return
    }

    // If no phone, ensure notifications are off
    if (!data.phone && data.accepts_notifications) {
      data.accepts_notifications = false
    }

    await onSubmit({
      name: data.name,
      email: data.email || undefined,
      phone: data.phone || undefined,
      country_code: data.country_code,
      relationship: data.relationship as RelationshipType,
      is_primary_contact: data.is_primary_contact,
      accepts_notifications: data.accepts_notifications,
    })
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-sm font-medium text-gray-900 mb-4">
        {isEditing ? 'Editar Familiar' : 'Adicionar Familiar'}
      </h4>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Completo *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do familiar" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="relationship"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parentesco *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {RELATIONSHIP_SELECT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email@exemplo.com (opcional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>WhatsApp</FormLabel>
                  <FormControl>
                    <PhoneInputWithDDI
                      value={field.value ?? ''}
                      countryCode={form.watch('country_code')}
                      onPhoneChange={field.onChange}
                      onCountryCodeChange={(code) => form.setValue('country_code', code)}
                      error={fieldState.error?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="is_primary_contact"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      if (checked && !isEditing && existingPrimaryCount >= 2) {
                        form.setError('is_primary_contact', {
                          message: 'Maximo de 2 contatos principais por paciente',
                        })
                        return
                      }
                      form.clearErrors('is_primary_contact')
                      field.onChange(checked)
                    }}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="font-normal">Contato Primario</FormLabel>
                  <p className="text-xs text-gray-500">
                    Este familiar sera o contato principal para receber comunicacoes
                  </p>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="accepts_notifications"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-200 p-3">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm font-medium flex items-center gap-2">
                    {field.value ? (
                      <Bell className="h-4 w-4 text-green-600" />
                    ) : (
                      <BellOff className="h-4 w-4 text-gray-400" />
                    )}
                    Notificacoes WhatsApp
                  </FormLabel>
                  <FormDescription className="text-xs">
                    {hasPhone
                      ? 'Cadastro mantido para comunicacoes futuras; envios automaticos estao pausados'
                      : 'Numero de WhatsApp e obrigatorio para ativar notificacoes'}
                  </FormDescription>
                  <FormMessage />
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      if (checked && !hasPhone) {
                        form.setError('accepts_notifications', {
                          message: 'Numero de WhatsApp e obrigatorio para ativar notificacoes',
                        })
                        return
                      }
                      form.clearErrors('accepts_notifications')
                      field.onChange(checked)
                    }}
                    disabled={!hasPhone}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Salvar' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
